import {
  VStack,
  Text,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Badge,
  Divider,
  List,
  ListItem,
  Progress,
  HStack,
} from '@chakra-ui/react'
import { MdCheckCircle, MdWarning, MdInfo, MdScience } from 'react-icons/md'

interface AIResultsPanelProps {
  hasResults: boolean
  analyzing: boolean
  specialtyId: string
}

// Mock results data based on specialty
const getMockResults = (specialtyId: string) => {
  const results: Record<string, any> = {
    parasitology: {
      title: 'Parasitology Analysis',
      findings: [
        { type: 'positive', text: 'Giardia lamblia cysts detected', confidence: 92 },
        { type: 'info', text: 'Sample quality: Excellent', confidence: 100 },
        { type: 'warning', text: 'Recommend confirmatory testing', confidence: 85 },
      ],
      summary: 'Parasitic infection detected. Clinical correlation recommended.',
    },
    radiology: {
      title: 'Radiology Analysis',
      findings: [
        { type: 'positive', text: 'No fractures detected', confidence: 95 },
        { type: 'info', text: 'Bone density: Normal', confidence: 88 },
        { type: 'info', text: 'Soft tissue: Unremarkable', confidence: 90 },
      ],
      summary: 'Normal radiographic findings. No acute pathology identified.',
    },
    hematology: {
      title: 'Hematology Analysis',
      findings: [
        { type: 'positive', text: 'WBC count: Within normal limits', confidence: 94 },
        { type: 'warning', text: 'Mild anemia detected (Hb: 11.2 g/dL)', confidence: 96 },
        { type: 'info', text: 'Platelet count: Normal', confidence: 93 },
      ],
      summary: 'Mild normocytic anemia present. Further investigation recommended.',
    },
    dermatology: {
      title: 'Dermatology Analysis',
      findings: [
        { type: 'info', text: 'Lesion characteristics: Regular borders', confidence: 89 },
        { type: 'positive', text: 'No malignant features detected', confidence: 87 },
        { type: 'info', text: 'ABCDE score: Low risk', confidence: 91 },
      ],
      summary: 'Benign-appearing lesion. Routine monitoring advised.',
    },
  }

  return results[specialtyId] || results.parasitology
}

export default function AIResultsPanel({
  hasResults,
  analyzing,
  specialtyId,
}: AIResultsPanelProps) {
  if (!hasResults && !analyzing) {
    return (
      <VStack spacing={4} py={8} color="gray.500">
        <MdScience style={{ fontSize: '64px' }} />
        <Text textAlign="center">
          Upload images to start AI analysis
        </Text>
      </VStack>
    )
  }

  if (analyzing) {
    return (
      <VStack spacing={4} py={8}>
        <Progress size="sm" isIndeterminate colorScheme="purple" w="100%" />
        <Text color="gray.600">Processing images...</Text>
      </VStack>
    )
  }

  const results = getMockResults(specialtyId)

  const getIcon = (type: string) => {
    switch (type) {
      case 'positive':
        return MdCheckCircle
      case 'warning':
        return MdWarning
      default:
        return MdInfo
    }
  }

  const getColor = (type: string) => {
    switch (type) {
      case 'positive':
        return 'green'
      case 'warning':
        return 'orange'
      default:
        return 'blue'
    }
  }

  return (
    <VStack spacing={4} align="stretch">
      {/* Analysis Summary */}
      <Card bg="purple.50" borderColor="purple.200" borderWidth={1}>
        <CardHeader pb={2}>
          <HStack justify="space-between">
            <Heading size="sm">{results.title}</Heading>
            <Badge colorScheme="purple">AI Analyzed</Badge>
          </HStack>
        </CardHeader>
        <CardBody pt={2}>
          <Text fontSize="sm">{results.summary}</Text>
        </CardBody>
      </Card>

      {/* Findings */}
      <Card>
        <CardHeader>
          <Heading size="sm">Findings</Heading>
        </CardHeader>
        <Divider />
        <CardBody>
          <List spacing={4}>
            {results.findings.map((finding: any, index: number) => {
              const IconComponent = getIcon(finding.type)
              return (
                <ListItem key={index}>
                  <VStack align="stretch" spacing={2}>
                    <HStack>
                      <IconComponent style={{ fontSize: '20px', color: `var(--chakra-colors-${getColor(finding.type)}-500)` }} />
                      <Text fontSize="sm" flex={1}>
                        {finding.text}
                      </Text>
                    </HStack>
                    <HStack spacing={2}>
                      <Text fontSize="xs" color="gray.500">
                        Confidence:
                      </Text>
                      <Progress
                        value={finding.confidence}
                        size="sm"
                        colorScheme={getColor(finding.type)}
                        flex={1}
                        borderRadius="full"
                      />
                      <Text fontSize="xs" color="gray.600" fontWeight="semibold">
                        {finding.confidence}%
                      </Text>
                    </HStack>
                  </VStack>
                </ListItem>
              )
            })}
          </List>
        </CardBody>
      </Card>

      {/* Disclaimer */}
      <Card bg="yellow.50" borderColor="yellow.200" borderWidth={1}>
        <CardBody>
          <HStack spacing={2}>
            <MdWarning style={{ fontSize: '20px', color: 'var(--chakra-colors-yellow-600)' }} />
            <Text fontSize="xs" color="yellow.800">
              <strong>Disclaimer:</strong> AI results are for reference only. Always consult with a medical professional for diagnosis.
            </Text>
          </HStack>
        </CardBody>
      </Card>
    </VStack>
  )
}
